import { getChildProgress, startPlaySession } from '../../../shared/api/play'
import type { StaffAuth } from '../../../shared/api/teaching'
import { getDeviceId } from '../../../shared/stores/device'
import { setKidSession } from '../../../shared/stores/kidSession'
import { beginChild, seedFromDurable } from '../../../shared/stores/kidProgress'
import { clearPlayHandoff, setPlayHandoff, setPlayReturn } from '../../../shared/stores/playHandoff'
import { pathFor } from '../../../app/router/routes'
import type { GameId } from '../../../shared/curriculum'

/**
 * Teacher Play Mode — the four writes that turn a teacher's tap into a child's
 * round, in the one order that is safe.
 *
 * 🔴 IT LIVES IN ITS OWN MODULE BECAUSE TWO SCREENS LAUNCH AND THE ORDER IS
 * LOAD-BEARING. D3 confirms a child and a game; the play-setup register offers
 * the same launch beside the roll number a teacher is already looking at. Two
 * copies of this sequence is two chances to write the session before the
 * progress mirror, and the symptom of getting it wrong is the previous child's
 * coins and stickers appearing on this child's home screen.
 *
 * 🔴 AND IT IS DELIBERATELY THE SAME SEQUENCE `KidApp` RUNS AFTER A CHILD SIGNS
 * THEMSELVES IN. `onSignedIn` there does exactly this: `setKidSession`, then
 * `beginChild` with the roster profile. A second, subtly different launch path
 * is how "played as the wrong child" gets in — so this one calls the same
 * stores with the same payload, and the only additions are the two handoff
 * values the child's flow has no other way to learn.
 */

export interface LaunchInput {
  playerId: string
  /**
   * The game the teacher picked, or `null` to leave the child on the home
   * screen.
   *
   * ⚠️ `null` IS A REAL CHOICE RATHER THAN A MISSING ONE. The register's quick
   * launch has no game picker on it — a teacher going down a roll list wants the
   * child playing, and the child's own home screen is a better chooser than a
   * dropdown in a table cell.
   */
  gameId: GameId | null
  auth: StaffAuth
  /**
   * Where the ADULT is returned to when the tablet comes back. The teacher's own
   * session never ended, so the door picker would be a detour through a sign-in
   * form they do not need.
   */
  returnTo?: string
}

/**
 * Mint the child's session and hand the tablet over.
 *
 * 🔴 IT NAVIGATES ON SUCCESS AND THROWS ON FAILURE — never both, and never
 * neither. A 403 (a child outside this teacher's classes), a 400 (a child in no
 * class, or an archived one) and a network drop all reach the caller as an
 * `ApiError` so the screen can say something true. What must not happen is a
 * navigation into `KidApp` with no session, which is the sign-in screen wearing
 * a launch button's consequences.
 *
 * ⚠️ NOTHING IS WRITTEN BEFORE THE SERVER AGREES. The handoff and the return
 * route are set only after the mint returns, so a refused launch leaves no game
 * armed for the next child on this laptop — and `clearPlayHandoff` scrubs a
 * stale one from an earlier attempt in the same breath.
 */
export async function launchPlayAs(input: LaunchInput): Promise<void> {
  /*
   * ⚠️ THE TABLET'S OWN UUID, NOT A FRESH ONE. `getDeviceId` mints on first read
   * and never re-mints; the `Device` row the server writes is keyed by it, which
   * is what makes an admin's `DELETE /devices/:id` reach a teacher-launched
   * session exactly as it reaches a child's own.
   */
  const deviceId = getDeviceId()

  clearPlayHandoff()
  const session = await startPlaySession(input.playerId, deviceId, input.auth)

  /*
   * 🔴 SESSION FIRST, THEN THE PROGRESS MIRROR, THEN THE HANDOFF. `beginChild`
   * is what resets the local coins/stickers/unlocks to this child's own — the
   * call `KidApp` describes as the fix for *"the second child of the morning
   * inheriting the first one's"*. Setting the handoff before it would let a
   * fast render open a game against the outgoing child's mirror.
   */
  setKidSession(session)

  /*
   * 🔴 THE DURABLE COPY, FETCHED BEFORE THE MIRROR IS POINTED AT THE CHILD.
   * `beginChild` starts an unseen child at zero, and the round that follows
   * writes that zero back as a snapshot — which is how a child's coins, stickers
   * and streak disappeared the first time a teacher launched them on a laptop
   * another child had used. The read costs one request on a path that has just
   * proved the network works.
   *
   * ⚠️ AND IT IS NOT ALLOWED TO FAIL THE LAUNCH. A refused or slow read leaves
   * `durable` undefined and the child plays from an empty mirror exactly as
   * before — worse data, but a working tablet, which is the trade
   * offline-sync.md §1 makes everywhere else in this file.
   */
  beginChild(session.playerId, { name: session.name, ageBand: session.ageBand })

  /*
   * 🔴 THEN THE CHILD'S DURABLE COPY, BEFORE THE TABLET CHANGES HANDS.
   * `beginChild` starts an unseen child at zero, and the round that follows
   * writes that zero back as a snapshot — which is how a child's coins, stickers
   * and streak vanished the first time a teacher launched them on a laptop
   * another child had used. `seedFromDurable` fills the mirror it just claimed.
   *
   * ⚠️ AWAITED HERE, UNLIKE IN `KidApp`, AND THE ASYMMETRY IS THE POINT. This
   * is an adult pressing Play and watching a button, not a child mid-round —
   * *"gameplay never awaits the network"* is about the second. One request on a
   * path that has just proved the network works buys a mirror that is already
   * right when the game mounts.
   *
   * ⚠️ AND IT MAY NOT FAIL THE LAUNCH. A refused or slow read leaves the mirror
   * empty and the child plays exactly as they did before this line existed:
   * worse data, working tablet.
   */
  await getChildProgress(session.playerId, session.deviceToken)
    .then((durable) => seedFromDurable(session.playerId, durable))
    .catch(() => undefined)

  if (input.gameId !== null) {
    setPlayHandoff({ playerId: session.playerId, gameId: input.gameId })
  }
  if (input.returnTo !== undefined) setPlayReturn(input.returnTo)

  /*
   * ⚠️ A HASH WRITE RATHER THAN A `navigate` CALLBACK. This module is reached
   * from two screens, neither of which is given the router's navigator — and
   * `KidApp` itself leaves the same way (`window.location.hash = pathFor(...)`),
   * so this is the tier's existing idiom rather than a new one.
   */
  window.location.hash = pathFor('kid')
}
